import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { serviceLinks } from "@/lib/service-links";

const links = [
    { label: "Process", href: "#process" },
    { label: "Impact", href: "#impact" },
    { label: "Contact", href: "#contact" },
];

function ServiceMenuItems({ onSelect, variant = "desktop" }) {
    const itemClass =
        variant === "desktop"
            ? "group flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors duration-200 hover:bg-white/5"
            : "group flex w-full flex-col items-start gap-1 py-4 text-left border-b border-white/5";

    return (
        <>
            {serviceLinks.map((item) => (
                <li key={item.label} role="none">
                    <button
                        type="button"
                        role="menuitem"
                        data-testid={`nav-service-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => onSelect(item)}
                        className={itemClass}
                    >
                        <span
                            className={`font-mono-tech uppercase tracking-[0.18em] text-white group-hover:text-[#FF3B30] transition-colors duration-200 ${
                                variant === "desktop" ? "text-xs" : "text-sm"
                            }`}
                        >
                            {item.label}
                        </span>
                        <span
                            className={`leading-snug text-slate-500 group-hover:text-slate-400 transition-colors duration-200 ${
                                variant === "desktop" ? "text-[11px]" : "text-xs"
                            }`}
                        >
                            {item.description}
                        </span>
                    </button>
                </li>
            ))}
        </>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const servicesRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const onWebsitesPage = location.pathname === "/websites";
    const onAskboxPage = location.pathname === "/askbox";
    const onOfferPage = onWebsitesPage || onAskboxPage;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setServicesOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setServicesOpen(false);
                setMobileOpen(false);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    useEffect(() => {
        if (!servicesOpen) return undefined;

        const onClickOutside = (event) => {
            if (servicesRef.current && !servicesRef.current.contains(event.target)) {
                setServicesOpen(false);
            }
        };

        // Defer so the same click that opened the menu does not close it immediately.
        const timerId = window.setTimeout(() => {
            document.addEventListener("click", onClickOutside);
        }, 0);

        return () => {
            window.clearTimeout(timerId);
            document.removeEventListener("click", onClickOutside);
        };
    }, [servicesOpen]);

    const goHome = () => {
        setMobileOpen(false);
        if (location.pathname === "/") {
            window.__lenis?.scrollTo(0, { duration: 1.4 });
        } else {
            navigate("/");
        }
    };

    const goToSection = (href) => {
        setMobileOpen(false);
        if (href.startsWith("/")) {
            navigate(href);
            return;
        }
        if (location.pathname === "/") {
            scrollToId(href);
        } else {
            navigate(`/${href}`);
        }
    };

    const handleServiceClick = (item) => {
        setServicesOpen(false);
        setMobileOpen(false);
        if (item.kind === "external") {
            window.location.href = item.href;
            return;
        }
        if (item.kind === "static") {
            window.location.href = item.href;
            return;
        }
        if (item.href.startsWith("/")) {
            navigate(item.href);
            return;
        }
        goToSection(item.href);
    };

    const handleCta = () => {
        setMobileOpen(false);
        if (onAskboxPage) {
            navigate("/askbox/demo");
            return;
        }
        if (onWebsitesPage) {
            scrollToId("#get-started");
        } else {
            goToSection("#contact");
        }
    };

    return (
        <>
            <motion.header
                data-testid="navbar"
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed top-0 left-0 right-0 z-[90] px-4 sm:px-8 lg:px-16 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "backdrop-blur-xl bg-[#07090E]/80 border-b border-white/10"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="flex items-center justify-between py-4 max-w-[1400px] mx-auto gap-3">
                    <button
                        data-testid="nav-logo"
                        onClick={goHome}
                        className="flex items-center shrink-0"
                        aria-label="SalesCan home"
                    >
                        <img src="/salescan-logo.png" alt="SalesCan" className="h-8 sm:h-9 w-auto" />
                    </button>

                    <nav className="hidden md:flex items-center gap-8">
                        <div
                            ref={servicesRef}
                            className="relative"
                            onMouseEnter={() => setServicesOpen(true)}
                        >
                            <button
                                type="button"
                                data-testid="nav-link-services"
                                aria-expanded={servicesOpen}
                                aria-haspopup="true"
                                onClick={() => setServicesOpen((open) => !open)}
                                className="inline-flex items-center gap-1.5 font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-colors duration-300"
                            >
                                Services
                                <ChevronDown
                                    className={`w-3.5 h-3.5 transition-transform duration-300 ${
                                        servicesOpen ? "rotate-180 text-white" : ""
                                    }`}
                                />
                            </button>

                            <AnimatePresence>
                                {servicesOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 8 }}
                                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                                        className="absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-3 before:absolute before:inset-x-0 before:-top-3 before:h-3 before:content-['']"
                                        role="menu"
                                        aria-label="Services"
                                    >
                                        <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0E121B]/95 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-xl">
                                            <ul className="py-2">
                                                <ServiceMenuItems
                                                    onSelect={handleServiceClick}
                                                    variant="desktop"
                                                />
                                            </ul>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {!onOfferPage &&
                            links.map((l) => (
                                <button
                                    key={l.href}
                                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                                    onClick={() => goToSection(l.href)}
                                    className="font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-colors duration-300"
                                >
                                    {l.label}
                                </button>
                            ))}
                        {onOfferPage && (
                            <Link
                                to="/"
                                data-testid="nav-link-home"
                                className="font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-colors duration-300"
                            >
                                Home
                            </Link>
                        )}
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            data-testid="nav-cta-button"
                            onClick={handleCta}
                            className="group flex items-center gap-2 bg-[#FF3B30] hover:bg-[#ff5147] text-white text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2.5 rounded-full transition-colors duration-300 shadow-[0_0_24px_rgba(255,59,48,0.35)] hover:shadow-[0_0_40px_rgba(255,59,48,0.6)]"
                        >
                            <span className="hidden min-[420px]:inline">
                                {onAskboxPage
                                    ? "Demo Site"
                                    : onWebsitesPage
                                      ? "Get My $199 Website"
                                      : "Book a free consultation"}
                            </span>
                            <span className="min-[420px]:hidden">
                                {onAskboxPage ? "Demo" : onWebsitesPage ? "$199 site" : "Book call"}
                            </span>
                            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>

                        <button
                            type="button"
                            data-testid="nav-menu-button"
                            aria-label={mobileOpen ? "Close menu" : "Open menu"}
                            aria-expanded={mobileOpen}
                            onClick={() => setMobileOpen((open) => !open)}
                            className="md:hidden flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-200 transition-colors duration-300 hover:border-white/30 hover:text-white"
                        >
                            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            onClick={() => setMobileOpen(false)}
                            className="fixed inset-0 z-[100] bg-[#07090E]/75 backdrop-blur-sm md:hidden"
                        />
                        <motion.aside
                            data-testid="nav-mobile-drawer"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className="fixed top-0 right-0 bottom-0 z-[110] flex w-[88%] max-w-sm flex-col border-l border-white/10 bg-[#0E121B] md:hidden"
                        >
                            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                                <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-400">
                                    Menu
                                </span>
                                <button
                                    type="button"
                                    data-testid="nav-menu-close"
                                    aria-label="Close menu"
                                    onClick={() => setMobileOpen(false)}
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-200"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <nav className="flex-1 overflow-y-auto px-5 py-6">
                                <p className="mb-3 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-slate-500">
                                    Services
                                </p>
                                <ul className="mb-8">
                                    <ServiceMenuItems onSelect={handleServiceClick} variant="mobile" />
                                </ul>

                                {!onOfferPage && (
                                    <>
                                        <p className="mb-3 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-slate-500">
                                            Company
                                        </p>
                                        <div className="space-y-1">
                                            {links.map((l) => (
                                                <button
                                                    key={l.href}
                                                    type="button"
                                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                                    onClick={() => goToSection(l.href)}
                                                    className="block w-full py-3 text-left font-mono-tech text-sm uppercase tracking-[0.18em] text-slate-300 hover:text-white"
                                                >
                                                    {l.label}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {onOfferPage && (
                                    <button
                                        type="button"
                                        data-testid="nav-mobile-link-home"
                                        onClick={goHome}
                                        className="block w-full py-3 text-left font-mono-tech text-sm uppercase tracking-[0.18em] text-slate-300 hover:text-white"
                                    >
                                        Home
                                    </button>
                                )}
                            </nav>

                            <div className="border-t border-white/10 p-5">
                                <button
                                    type="button"
                                    data-testid="nav-mobile-cta-button"
                                    onClick={handleCta}
                                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF3B30] px-5 py-3 text-sm font-semibold text-white"
                                >
                                    {onAskboxPage
                                        ? "Demo Site"
                                        : onWebsitesPage
                                          ? "Get My $199 Website"
                                          : "Book a free consultation"}
                                    <ArrowUpRight className="h-4 w-4" />
                                </button>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
